/**
 * Product analytics → GA4.
 *
 * Replaces the PostHog client this app used to run. The GA4 tag is loaded by the
 * snippet in index.html, and the inline `gtag()` shim it defines queues calls
 * into `dataLayer` before gtag.js itself arrives — so unlike the old lazy-loaded
 * PostHog client, there is no init race and nothing here needs its own queue.
 *
 * Everything is best-effort: when the tag is blocked or hasn't parsed, these
 * calls no-op rather than throwing into the caller's render path.
 */

import { hasConsent } from "@/lib/consent";

type GtagFn = (...args: unknown[]) => void;

/** GA4 limits: event/param names ≤40 chars, string values ≤100. */
const MAX_NAME_LENGTH = 40;
const MAX_VALUE_LENGTH = 100;

const withGtag = (fn: (gtag: GtagFn) => void) => {
  if (typeof window === "undefined") return;
  // `window.gtag` exists even before consent — src/lib/consent.ts installs the
  // shim up front so it can declare Consent Mode defaults. Its presence is
  // therefore not evidence of an opt-in, so check the consent state itself,
  // or analytics events would queue in dataLayer for a visitor who said no.
  if (!hasConsent("analytics")) return;
  const gtag = window.gtag;
  if (typeof gtag !== "function") return;
  try {
    fn(gtag);
  } catch (error) {
    console.warn("Analytics event failed:", error);
  }
};

/**
 * GA4 only accepts letters, numbers and underscores, and names must start with a
 * letter. PostHog-style `$`-prefixed keys (`$current_url`) would be dropped
 * silently, so normalise instead of trusting the caller.
 */
const sanitizeName = (name: string): string =>
  name
    .replace(/[^a-zA-Z0-9_]/g, "_")
    .replace(/^[^a-zA-Z]+/, "")
    .slice(0, MAX_NAME_LENGTH);

const sanitizeValue = (value: unknown): string | number | boolean | undefined => {
  if (value === null || value === undefined) return undefined;
  if (typeof value === "number" || typeof value === "boolean") return value;
  if (typeof value === "string") return value.slice(0, MAX_VALUE_LENGTH);
  // GA4 takes scalars only; flatten anything else rather than dropping it.
  try {
    return JSON.stringify(value).slice(0, MAX_VALUE_LENGTH);
  } catch {
    return undefined;
  }
};

const sanitizeParams = (props?: Record<string, unknown>): Record<string, unknown> => {
  if (!props) return {};
  const params: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(props)) {
    const sanitized = sanitizeValue(value);
    if (sanitized === undefined) continue;
    const name = sanitizeName(key);
    if (name) params[name] = sanitized;
  }
  return params;
};

/** Emit a custom event. SSR-safe — no-ops on the server. */
export const captureEvent = (eventName: string, properties?: Record<string, unknown>) => {
  withGtag((gtag) => gtag("event", sanitizeName(eventName), sanitizeParams(properties)));
};

/**
 * Tie subsequent events to a stable user id, so GA4 can stitch sessions across
 * devices. Deliberately takes ONLY the id: Google's terms forbid sending
 * personally identifiable information to GA4, so the email the PostHog version
 * attached here must not follow it over.
 */
export const identifyUser = (userId: string) => {
  withGtag((gtag) => gtag("set", { user_id: userId }));
};

/** Drop the user association on sign-out so the next visitor isn't merged in. */
export const resetUser = () => {
  withGtag((gtag) => gtag("set", { user_id: null }));
};
